/**
 * TOSS Web Application - Sidebar navigation configuration
 *
 * Navigation item type, the navigation items tree and the permission-based
 * visibility helper. Extracted verbatim from Sidebar.tsx.
 */

import type { I18nKey } from '@/i18n/defaults';
import { UserRole } from '@/types';
import { PK } from '@/constants/permissions';
import {
  type LucideIcon,
  FileText,
  ClipboardCheck,
  ListChecks,
  Wallet,
  Banknote,
  Users,
  Contact,
  UserX,
  Library,
  Building2,
  Container,
  Zap,
  ConciergeBell,
  MapPin,
  Route,
  Package,
  TrainFront,
  Warehouse,
  Tags,
  BookOpen,
  Settings,
  ShieldCheck,
  ScrollText,
  Scale,
  FileSignature,
  Mail,
  Boxes,
  ClipboardX,
  UserCog,
  ArrowLeftRight,
} from 'lucide-react';

/**
 * Navigation item definition
 */
export interface NavItem {
  /** Unique identifier */
  id: string;
  /** Display label (fallback when labelKey is not set or unresolved) */
  label: string;
  /** i18n key resolved at render time; overrides label when provided */
  labelKey?: I18nKey;
  /** Route path */
  path: string;
  /** Icon component */
  icon: LucideIcon;
  /** Roles that can see this item (empty = falls through to permission checks) */
  allowedRoles?: UserRole[];
  /** Granular permission key required to render this item. */
  permission?: string;
  /** Visible if any of these granular permission keys are granted (OR). */
  anyOf?: string[];
  /** Child navigation items for submenus */
  children?: NavItem[];
}


// ============================================================================
// Navigation Configuration
// ============================================================================

/**
 * Navigation items configuration
 * Based on PROJECT_DOCUMENTATION.md Section 4.1
 */
export const navigationItems: NavItem[] = [
  {
    id: 'applications',
    label: 'Заявки',
    path: '/applications',
    icon: FileText,
    permission: PK.Applications.View,
  },
  {
    id: 'pto',
    label: 'ПТО',
    path: '/pto',
    icon: ClipboardCheck,
    permission: PK.Fmo.PtoView,
  },
  {
    id: 'tasks',
    label: 'Задачи',
    path: '/tasks',
    icon: ListChecks,
    anyOf: [PK.Tasks.ViewOwn, PK.Tasks.ViewAll],
  },
  {
    id: 'expenses',
    label: 'Расходы',
    path: '/expenses',
    icon: Wallet,
    anyOf: [PK.Expenses.OwnView, PK.Expenses.AllView],
  },
  {
    id: 'workers',
    label: 'Сотрудники',
    path: '/workers',
    icon: Users,
    permission: PK.Workers.View,
    children: [
      {
        id: 'workers-list',
        label: 'Список',
        path: '/workers',
        icon: Contact,
        permission: PK.Workers.View,
      },
      {
        id: 'deletion-requests',
        label: 'Запросы на удаление',
        path: '/workers/deletion-requests',
        icon: UserX,
        anyOf: [PK.Workers.DeleteRequestApprove, PK.Workers.DeleteRequestReject],
      },
    ],
  },
  {
    id: 'directory',
    label: 'Справочники',
    path: '/directory',
    icon: Library,
    // Show if user has access to any directory module — handled via children
    children: [
      {
        id: 'customers',
        label: 'Клиенты',
        path: '/directory/customers',
        icon: Building2,
        permission: PK.Customers.View,
      },
      {
        id: 'containers',
        label: 'Контейнеры',
        path: '/directory/containers',
        icon: Container,
        permission: PK.Containers.View,
      },
      {
        id: 'equipment',
        label: 'Энергетическая установка',
        labelKey: 'equipment.label',
        path: '/directory/equipment',
        icon: Zap,
        permission: PK.Equipment.View,
      },
      {
        id: 'services',
        label: 'Услуги',
        path: '/directory/services',
        icon: ConciergeBell,
        permission: PK.Services.View,
      },
      {
        id: 'pto-directory',
        label: 'ПТО',
        path: '/directory/pto',
        icon: MapPin,
        permission: PK.Pto.View,
      },
      {
        id: 'directions',
        label: 'Направления',
        path: '/directory/directions',
        icon: Route,
        permission: PK.Directions.View,
      },
      {
        id: 'materials',
        label: 'Материалы',
        path: '/directory/materials',
        icon: Package,
        permission: PK.Materials.View,
      },
      {
        id: 'stations',
        label: 'Станции',
        path: '/directory/stations',
        icon: TrainFront,
        permission: PK.Stations.View,
      },
      {
        id: 'terminals',
        label: 'Терминалы',
        path: '/directory/terminals',
        icon: Warehouse,
        permission: PK.Terminals.View,
      },
      {
        id: 'pricing',
        label: 'Прайс-лист',
        path: '/directory/pricing',
        icon: Tags,
        permission: PK.Pricing.View,
      },
      {
        id: 'container-attributes',
        label: 'Типы и размеры контейнеров',
        path: '/directory/container-attributes',
        icon: Boxes,
        permission: PK.ContainerAttributes.View,
      },
      {
        id: 'inspection-reasons',
        label: 'Причины осмотра',
        path: '/directory/inspection-reasons',
        icon: ClipboardX,
        permission: PK.InspectionReasons.View,
      },
      {
        id: 'mechanic-types',
        label: 'Типы исполнителей',
        path: '/directory/mechanic-types',
        icon: UserCog,
        permission: PK.MechanicTypes.View,
      },
    ],
  },
  {
    id: 'payroll',
    label: 'Сводная ведомость ЗП',
    path: '/payroll',
    icon: Banknote,
    permission: PK.Payroll.View,
  },
  {
    id: 'instructions',
    label: 'Инструкции',
    path: '/instructions',
    icon: BookOpen,
    permission: PK.Instructions.View,
  },
  {
    id: 'admin',
    label: 'Администрирование',
    path: '/admin',
    icon: Settings,
    // Visibility computed from children (any granted child reveals the parent)
    children: [
      {
        id: 'permissions',
        label: 'Права доступа',
        path: '/admin/permissions',
        icon: ShieldCheck,
        permission: PK.Permissions.MatrixView,
      },
      {
        id: 'audit-logs',
        label: 'Журнал аудита',
        path: '/admin/audit-logs',
        icon: ScrollText,
        permission: PK.AuditLogs.View,
      },
      {
        id: 'legal-documents',
        label: 'Юр. документы',
        path: '/admin/legal-documents',
        icon: Scale,
        permission: PK.Legal.Manage,
      },
      {
        id: 'document-settings',
        label: 'Шапка документов',
        path: '/admin/document-settings',
        icon: FileSignature,
        permission: PK.Settings.Manage,
      },
      {
        id: 'email-gateway',
        label: 'Почтовый шлюз',
        path: '/admin/email-gateway',
        icon: Mail,
        permission: PK.EmailGateway.View,
      },
      {
        id: 'integration-1c',
        label: 'Обмен с 1С',
        path: '/admin/integrations/1c',
        icon: ArrowLeftRight,
        permission: PK.Settings.Manage,
      },
    ],
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a navigation item is visible for the current user role.
 *
 * Drives off the granular permission keys returned by
 * `GET /api/lk/permissions/my-keys` (Set<string> in `usePermissionStore.keys`),
 * so flipping a checkbox in the admin matrix immediately reflects in the menu
 * once the affected user reloads their permissions.
 */
export function isNavItemVisible(
  item: NavItem,
  userRole: UserRole | null,
  keys: Set<string> | null
): boolean {
  if (!userRole) return false;

  // Hard role gate (kept for cases where a screen is wired to a fixed role list).
  if (item.allowedRoles && item.allowedRoles.length > 0) {
    return item.allowedRoles.includes(userRole);
  }

  // Until the user's keys load, fall back to "hidden". PermissionRoute also
  // shows a loading state, so the menu just stays empty for one tick.
  if (!keys) return false;

  if (item.permission) {
    return keys.has(item.permission);
  }

  if (item.anyOf && item.anyOf.length > 0) {
    return item.anyOf.some((k) => keys.has(k));
  }

  // No explicit permission/anyOf — visible iff at least one child is visible.
  if (item.children && item.children.length > 0) {
    return item.children.some((child) => isNavItemVisible(child, userRole, keys));
  }

  // Items with no permission/anyOf/children are always visible (rare).
  return true;
}
